import { initEditOffer } from '@pages-old/edit-offer/main';
import { translations } from '@tools/translations/translations';
import { createApp } from 'vue';
import {
  isFloat, isLength, isInt, isEmpty,
} from 'validator';
import { URL_SEARCH_NEAR_TRANSPORTS } from '@tools/routes/routes';
import requests from '@tools/requests-mixin';
import TwigFormValidator from '@tools/twig-form/validator';
import AutocompleteCustom from '@atoms/inputs/input-location';
import Logger from '@tools/logger/logger';
import eventBus from '@tools/event-bus';
import googlePlacesApiUtils, {
  ERR_ZIPCODE,
  ERR_STREET_NR,
} from '@molecules/google-places-api-utils';
import OFFICE_TYPE from '@organisms/offer/office-type-enum';

/**
 * Init create offer step intro Vue
 */
export default function () {
  const initCapacityElement = document.getElementById('setup_data_capacity');
  const initCapacityValue = initCapacityElement ? initCapacityElement.value : 0;

  const component = {
    delimiters: [ '${', '}' ],
    mixins: [ googlePlacesApiUtils, requests ],
    data() {
      const container = '.create-offer--step-intro';
      return {
        container,
        trad: {
          info: '',
          infos: '',
        },
        lines: [],
        setup: {
          surface: {
            max: 1000000,
          },
          price: {
            max: 1000000,
          },
          seatCapacity: {
            max: 2000,
          },
          standCapacity: {
            max: 2000,
          },
          capacity: {
            max: 2000,
          },
        },
        openspaceChecked: true,
        capacity: initCapacityValue,
        radioChoiceVisibility: true,
        googleSearchVisibility: false,
        addressIsValid: true,
        addressIsComplete: true,
        address: {
          // Binded into AddressFieldsType.php
          // space inside empty strings in order to properly display the input animation
          name:
            document.querySelector(`${container} .streetFormatted`)?.value
            || ' ',
          street_number:
            document.querySelector(`${container} .streetNumber`)?.value || '',
          route: document.querySelector(`${container} .street`)?.value || '',
          administrative_area_level_1:
            document.querySelector(`${container} .region`)?.value || ' ',
          locality: document.querySelector(`${container} .city`)?.value || ' ',
          postal_code:
            document.querySelector(`${container} .zipCode`)?.value || ' ',
          // Binded into GeolocalizableFormBuilder.php
          lat: document.querySelector(`${container} .latitude`)?.value || '',
          lng: document.querySelector(`${container} .longitude`)?.value || '',
        },
      };
    },
    mounted() {
      this.initGoogleSearchVisibility();
      this.initDisplay();
      this.initAutocomplete();
      // Triggered in atom input-location.js, when entry in search input.
      eventBus.addEventListener('input-location-change', (place) => {
        this.fillFormWithGoogleMapResult(place.detail.place);
      });

      eventBus.addEventListener('custom-autocomplete-triggered', () => {
        this.emptyHiddenFields(this.container);
      });
      initEditOffer();
    },
    methods: {
      /**
       * initAutocomplete
       * @description Init Google autocomplete address form
       */
      initAutocomplete() {
        const customAutocomplete = new AutocompleteCustom(
          document.getElementById('setup_data_address_location'),
        );
        customAutocomplete.enablePlaces();
        customAutocomplete.init();
        // Don't submit offer form when selecting address in autocomplete dropdown
        // and prevent form validator to highlight all form in red or premature submit
        // form
        this.$nextTick(() => {
          document
            .getElementById('setup_data_address_location')
            .addEventListener('keydown', (event) => {
              if (event.keyCode === 13) {
                event.preventDefault();
              }
            });
        });
      },
      /**
       * fillFormWithGoogleMapResult
       * @description Fill new address form with google autocomplete result
       * @param {object} place - data fetched from google api
       */
      fillFormWithGoogleMapResult(place) {
        this.resetFields(this.container, this.address);
        this.lines = [];
        Logger.info('Google result:', place);
        if (!place.name || !place.viewport) {
          this.addressIsValid = false;
          return;
        }
        this.addressIsComplete = false;
        this.addressIsValid = false;
        switch (this.checkPlace(place)) {
        case ERR_ZIPCODE:
          this.addressIsValid = false;
          this.triggerLocationError(ERR_ZIPCODE);
          return;

        case ERR_STREET_NR:
          this.addressIsComplete = false;
          this.triggerLocationError(ERR_STREET_NR);
          return;
        default:
          this.addressIsValid = true;
          this.addressIsComplete = true;
        }

        this.displayTransportLines(place);

        const validator = new TwigFormValidator({ formSelector: `${this.container} form` });
        validator.resetInputError(document.querySelector('[name=setup_data_address_location]'));

        this.address = this.setAddressFromPlace(place);

        Logger.info('display form fields');
      },
      /**
       * Display transport lines
       * @param place
       */
      displayTransportLines(place) {
        const parameters = {
          lat: place.location.latitude,
          lon: place.location.longitude,
        };
        this.lines = [];
        this.post(URL_SEARCH_NEAR_TRANSPORTS, parameters)
          .then((response) => response.data)
          .then((json) => {
            this.lines = json;
          })
          .catch(() => Logger.log('error catched'));
      },
      triggerLocationError(errorType) {
        const errMsg = errorType === ERR_ZIPCODE
          ? translations.validators['form.validation.google.address.error']
          : translations.validators['form.validation.google.address_no_number.error'];
        const validator = new TwigFormValidator({ formSelector: `${this.container} form` });
        validator.showInputError(document.querySelector('[name=setup_data_address_location]'), errMsg);
      },
      // Form Validation rules
      validateForm() {
        const validatorRules = {
          'setup[data][surface]': [
            (value) => this.openspaceChecked || isFloat(value, { min: 1 }),
            (value) => this.openspaceChecked || isFloat(value, { max: this.setup.surface.max }),
          ],
          'setup[data][capacity]': [
            (value) => isFloat(value, { min: 1 }),
            (value) => isFloat(value, { max: this.setup.capacity.max }),
          ],
          'setup[data][price]': (value) => isFloat(value, { min: 0, max: this.setup.price.max }),
          'setup[data][name]': (value) => isLength(value, { min: 1, max: 40 }),
          'setup[data][officeType]': 'required',
          'setup[data][standCapacity]': (value) => isInt(value, { min: 1, max: this.setup.standCapacity.max }),
          'setup[data][seatCapacity]': (value) => isInt(value, { min: 1, max: this.setup.seatCapacity.max }),
          setup_data_address_location: (value, input) => (!this.googleSearchVisibility || !isEmpty(value))
            || !input.getAttribute('data-isvalid')
            || !input.getAttribute('data-iscomplete'),
          'setup[data][address][choice][address]': 'required',
        };
        const validatorMessages = {
          'setup[data][surface]': [
            'required',
            translations.validators['form.validation.error.less_or_equal.number']
              .replace('{{ compared_value }}', this.setup.surface.max.toLocaleString('fr-FR')),
          ],
          'setup[data][capacity]': [
            'required',
            translations
              .validators['form.validation.error.less_or_equal.number']
              .replace('{{ compared_value }}', this.setup.capacity.max.toLocaleString('fr-FR')),
          ],
          'setup[data][price]': translations
            .validators['form.validation.error.less_or_equal.number']
            .replace('{{ compared_value }}', this.setup.price.max.toLocaleString('fr-FR')),
          'setup[data][seatCapacity]': translations
            .validators['form.validation.error.less_or_equal.number']
            .replace('{{ compared_value }}', this.setup.seatCapacity.max.toLocaleString('fr-FR')),
          'setup[data][standCapacity]': translations
            .validators['form.validation.error.less_or_equal.number']
            .replace('{{ compared_value }}', this.setup.standCapacity.max.toLocaleString('fr-FR')),
        };
        const validator = new TwigFormValidator({
          formSelector: `${this.container} form`,
          rules: validatorRules,
          messages: validatorMessages,
          onSuccess: (event) => {
            if (this.googleSearchVisibility) {
              if (document.getElementById('setup_data_address_fields_zipCode').value.trim() === '') {
                event.preventDefault();
                Logger.info('address has not zip code');
                this.triggerLocationError(ERR_ZIPCODE);
                return false;
              }
            }
            return true;
          },
        });
        validator.init();
      },
      /**
       * officeTypeChoice
       * @description change initial display rules on radio change.
       * Binded into SetupIntroType.php
       * @param {object} event - change
       */
      officeTypeChoice(event) {
        this.openspaceChecked = event.target.value === OFFICE_TYPE.OPEN_SPACE;
      },
      /**
       * initOpenSpaceChecked
       * @description Init conditional display of form, depending on Open Space Type checked or not
       */
      initOpenSpaceChecked() {
        const isOpenSpace = document.querySelector('#setup_data_officeType');
        if (isOpenSpace) {
          this.openspaceChecked = isOpenSpace.value === OFFICE_TYPE.OPEN_SPACE;
        }
      },
      /**
       * initDisplay
       * @description initial display rules
       */
      initDisplay() {
        this.validateForm();
        this.initOpenSpaceChecked();
        const addressSelect = document.getElementById(
          'setup_data_address_choice_address',
        );

        // Case only new address
        if (addressSelect && addressSelect.options.length === 1) {
          this.radioChoiceVisibility = false;
          this.googleSearchVisibility = true;
        }

        const newAddressContainer = document.querySelector(
          `${this.container} .new-address`,
        );
        if (newAddressContainer && newAddressContainer.checked) {
          this.googleSearchVisibility = true;
        }

        const newAddress = document.querySelector(
          '#setup_data_address_location',
        );
        if (newAddress.value !== '') {
          /**
           * New and existing address are handled in different inputs in Symfony Form Types...
           * Newly created address can not appear after edition hence this trick "coquin"
           * @see https://bureaux-a-partager.atlassian.net/browse/MATCH-6546
           */
          const newAddressOption = document.createElement('option');
          newAddressOption.innerHTML = newAddress.value;
          newAddressOption.selected = 'selected';
          addressSelect.insertBefore(newAddressOption, addressSelect.lastChild);
          newAddress.value = '';
        }

        this.initAddressStations();
      },
      /**
       * initAddressStations
       * @description display transport stations of default selected address
       */
      initAddressStations() {
        const selectedAddressAttributes = document.querySelector('#setup_data_address_choice_address option[selected="selected"]')?.dataset?.attr;

        if (!selectedAddressAttributes || selectedAddressAttributes === 'new-address') {
          return;
        }

        const selectedAddress = JSON.parse(selectedAddressAttributes);
        if (!selectedAddress?.lines) {
          return;
        }

        this.lines = selectedAddress.lines;
      },
      /**
       * addressDropdownChoice
       * @description change initial display rules on radio change.
       * Binded into AddressChoiceType.php
       * @param {object} event - change
       */
      addressDropdownChoice(event) {
        this.lines = [];
        const dataLocation = event.target.options[
          event.target.selectedIndex
        ].getAttribute('data-attr');

        if (dataLocation === null) {
          this.googleSearchVisibility = false;
          return;
        }

        if (dataLocation === 'new-address') {
          this.googleSearchVisibility = true;
          return;
        }

        let place = {};
        try {
          place = { location: JSON.parse(dataLocation) };
          this.googleSearchVisibility = false;
          this.displayTransportLines(place);
        } catch (e) {
          this.googleSearchVisibility = true;
        }
      },
      initGoogleSearchVisibility() {
        this.googleSearchVisibility = !document.querySelector(
          `${this.container} .row .create-offer-address__choice`,
        );
      },
    },
  };

  return createApp(component).mount('.create-offer--step-intro');
}
